import type React from "react";
import { useEffect, useState } from "react";

import { Stack, Typography } from "@mui/material";
import styled from "styled-components";

import { ChevronRightIcon } from "assets";
import { useAppConfigContext } from "contexts/AppConfigContext";
import { useProfileContext } from "contexts/ProfileContext";
import { Avatar } from "shared/atoms/Avatar";
import { NavBarDropdown } from "shared/NavBar/components/NavBarDropdown";
import { BLUE } from "theme/colors";

interface Props {
  isMobileNavigationOpen: boolean;
  setIsMobileNavigationOpen: (isMobileNavigationOpen: boolean) => void;
}

const BUTTON_HEIGHT = 58;

export const SubMenu: React.FC<Props> = ({ isMobileNavigationOpen, setIsMobileNavigationOpen }) => {
  const [isOpen, setOpen] = useState(false);
  const { navBarCollapsed } = useAppConfigContext();
  const { profile } = useProfileContext();
  const name = `${profile?.first_name} ${profile?.last_name}`;

  useEffect(() => {
    if (isMobileNavigationOpen === false) {
      setOpen(false);
    }
  }, [isMobileNavigationOpen]);

  return (
    <Container>
      <NavBarDropdownButton
        onClick={event => {
          event.stopPropagation();
          setOpen(!isOpen);
        }}
        $open={isOpen}
      >
        <ClickWrapper
          onClick={event => {
            event.stopPropagation();
            setIsMobileNavigationOpen(false);
            setOpen(!isOpen);
          }}
        >
          <NavBarDropdown open={isOpen} onClose={() => setOpen(false)} />
        </ClickWrapper>
        <Stack
          data-testid="header-dropdown-button"
          direction="row"
          justifyContent="center"
          alignItems="center"
          p={1}
          minHeight="40px"
          minWidth="40px"
          boxSizing="border-box"
        >
          <Avatar
            id={profile?.id}
            size={40}
            src={profile?.avatars?.medium || ""}
            name={name}
            color={BLUE[100]}
            backgroundColor={BLUE[15]}
          />
          {!navBarCollapsed && (
            <>
              <Name variant="subtitle2">{name}</Name>
              <Chevron $isOpen={isOpen} />
            </>
          )}
        </Stack>
      </NavBarDropdownButton>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  height: ${BUTTON_HEIGHT}px;
  min-height: ${BUTTON_HEIGHT}px;
  ${props => props.theme.belowBreakpoint} {
    bottom: 0px;
  }
`;

const NavBarDropdownButton = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  background: #fff;
  width: 100%;
  z-index: 99;
  box-sizing: border-box;
  border-radius: 12px;
  transition: 0.3s;
  border: 1px solid ${props => props.theme.colors.redesign.db20};
  box-shadow: ${props => (props.$open ? "0px 4px 12px rgba(56, 74, 95, 0.1)" : "none")};
  overflow: hidden;

  &:active {
    background-color: ${props => props.theme.colors.redesign.b10};
  }
`;

const Name = styled(Typography)`
  flex: 1;
  padding-left: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Chevron = styled(ChevronRightIcon)<{ $isOpen: boolean }>`
  padding: 8px;
  transform: ${props => (props.$isOpen ? "rotate(90deg)" : "rotate(-90deg)")};
  transition: 0.3s;
`;

const ClickWrapper = styled.div`
  width: 100%;
`;
